import { useState, useMemo, useCallback } from 'react';
import { useAccountsOnly } from './use-accounts-only';
import { useCampaignsByAccount } from './use-campaigns-by-account';
import { useAdSetsByCampaign } from './use-ad-sets-by-campaign';
import { useAdsByAdSet } from './use-ads-by-ad-set';

export interface ProgressiveLoadingActions {
  selectAccounts: (accountIds: string[]) => void;
  selectCampaigns: (campaignIds: string[]) => void;
  selectAdSets: (adSetIds: string[]) => void;
  clearAll: () => void;
}

export const useProgressiveMetaAdAccounts = ({ clientId, pause = false }: { clientId: string; pause?: boolean }) => {
  // State for selected items at each level
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);
  const [selectedAdSetIds, setSelectedAdSetIds] = useState<string[]>([]);

  // Step 1: Load accounts only (fast initial load)
  const accountsQuery = useAccountsOnly({ clientId, pause });

  // Step 2: Load campaigns when accounts are selected
  const campaignsQuery = useCampaignsByAccount({
    clientId,
    accountIds: selectedAccountIds,
    pause: pause || selectedAccountIds.length === 0,
  });

  // Step 3: Load ad sets when campaigns are selected
  const adSetsQuery = useAdSetsByCampaign({
    clientId,
    campaignIds: selectedCampaignIds,
    pause: pause || selectedCampaignIds.length === 0,
  });

  // Step 4: Load ads when ad sets are selected
  const adsQuery = useAdsByAdSet({
    clientId,
    adSetIds: selectedAdSetIds,
    pause: pause || selectedAdSetIds.length === 0,
  });

  // Actions for managing selections - wrapped with useCallback for stability
  const selectAccounts = useCallback((accountIds: string[]) => {
    setSelectedAccountIds(accountIds);
    // Clear dependent selections when accounts change
    setSelectedCampaignIds([]);
    setSelectedAdSetIds([]);
  }, []);

  const selectCampaigns = useCallback((campaignIds: string[]) => {
    setSelectedCampaignIds(campaignIds);
    // Clear dependent selections when campaigns change
    setSelectedAdSetIds([]);
  }, []);

  const selectAdSets = useCallback((adSetIds: string[]) => {
    setSelectedAdSetIds(adSetIds);
  }, []);

  const clearAll = useCallback(() => {
    setSelectedAccountIds([]);
    setSelectedCampaignIds([]);
    setSelectedAdSetIds([]);
  }, []);

  const actions: ProgressiveLoadingActions = {
    selectAccounts,
    selectCampaigns,
    selectAdSets,
    clearAll,
  };

  // Computed loading states
  const isLoading = accountsQuery.fetching || campaignsQuery.fetching || adSetsQuery.fetching || adsQuery.fetching;
  const hasError = accountsQuery.error || campaignsQuery.error || adSetsQuery.error || adsQuery.error;

  // Computed data combining all levels
  const combinedData = useMemo(() => {
    const accounts = accountsQuery.accounts;
    const campaigns = campaignsQuery.campaigns;
    const adSets = adSetsQuery.adSets;
    const ads = adsQuery.ads;

    // Return flat structure that matches what transformProgressiveDataToOriginalShape expects
    return {
      accounts: accounts || [],
      instagramAccounts: accountsQuery.instagramAccounts || [],
      campaigns: campaigns || [],
      adSets: adSets || [],
      ads: ads || [],
    };
  }, [
    accountsQuery.accounts,
    accountsQuery.instagramAccounts,
    campaignsQuery.campaigns,
    adSetsQuery.adSets,
    adsQuery.ads,
  ]);

  return {
    // Data
    data: combinedData,
    accounts: accountsQuery.accounts,
    instagramAccounts: accountsQuery.instagramAccounts,
    campaigns: campaignsQuery.campaigns,
    adSets: adSetsQuery.adSets,
    ads: adsQuery.ads,

    // Loading states
    isLoading,
    isAccountsLoading: accountsQuery.fetching,
    isCampaignsLoading: campaignsQuery.fetching,
    isAdSetsLoading: adSetsQuery.fetching,
    isAdsLoading: adsQuery.fetching,

    // Error states
    hasError,
    accountsError: accountsQuery.error,
    campaignsError: campaignsQuery.error,
    adSetsError: adSetsQuery.error,
    adsError: adsQuery.error,

    // Selection state
    selectedAccountIds,
    selectedCampaignIds,
    selectedAdSetIds,

    // Actions
    ...actions,

    // Raw query results for advanced usage
    accountsQuery,
    campaignsQuery,
    adSetsQuery,
    adsQuery,
  };
};
